import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { HiArrowUpRight } from "react-icons/hi2";
import api from "../../lib/api";
import useReveal from "../../hooks/useReveal";
import useSEO, { SITE_URL } from "../../seo/useSEO";
import { breadcrumb } from "../../seo/schemas";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return ""; }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  useReveal();

  useEffect(() => {
    let live = true;
    api
      .get(`/api/posts/${encodeURIComponent(slug)}`)
      .then(({ data }) => {
        if (!live) return;
        setPost(data.post);
        setStatus("ok");
      })
      .catch((e) => {
        if (!live) return;
        setStatus(e?.response?.status === 404 ? "404" : "error");
      });
    return () => { live = false; };
  }, [slug]);

  useSEO({
    title: post?.seo?.title
      ? `${post.seo.title} — Oguzhan Ozcelik`
      : post?.title
      ? `${post.title} — Oguzhan Ozcelik`
      : "Blog — Oguzhan Ozcelik",
    description: post?.seo?.description || post?.excerpt || "",
    path: `/blog/${slug}`,
    type: "article",
    image: post?.coverImage || undefined,
    jsonLd: post && {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id": `${SITE_URL}/blog/${post.slug}#article`,
          headline: post.title,
          description: post.seo?.description || post.excerpt,
          url: `${SITE_URL}/blog/${post.slug}`,
          mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
          author: { "@id": `${SITE_URL}/#person` },
          publisher: { "@id": `${SITE_URL}/#person` },
          datePublished: post.publishedAt,
          dateModified: post.updatedAt || post.publishedAt,
          articleSection: post.category || undefined,
          keywords: (post.seo?.keywords || post.tags || []).join(", "),
          image: post.coverImage || `${SITE_URL}/og-cover.jpg`,
          inLanguage: "en-US",
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: post.title, path: `/blog/${post.slug}` },
        ]),
      ],
    },
    jsonLdId: "blogpost-jsonld",
  });

  if (status === "404") return <Navigate to="/blog" replace />;

  return (
    <main className="section section--hero">
      <div className="container-x" style={{ maxWidth: 820 }}>
        <Link to="/blog" className="chip reveal" style={{ marginBottom: 20 }}>
          <span className="dot" /> Back to Blog
        </Link>

        {status === "loading" && <p className="text-muted">Loading post…</p>}
        {status === "error" && <p style={{ color: "var(--danger)" }}>Failed to load post.</p>}

        {post && (
          <article className="reveal">
            {post.category && (
              <Link
                to={`/blog?category=${encodeURIComponent(post.category)}`}
                className="chip"
                style={{ marginBottom: 16, textDecoration: "none" }}
              >
                <span className="dot" /> {post.category}
              </Link>
            )}
            <div className="text-muted" style={{ fontSize: 13, letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 16 }}>
              {fmtDate(post.publishedAt)} · {post.readingMinutes || 5} min read
            </div>
            <h1 className="about-hero__title" style={{ fontSize: "clamp(36px, 6vw, 80px)", marginBottom: 16 }}>
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="lead" style={{ marginTop: 8, marginBottom: 24 }}>
                {post.excerpt}
              </p>
            )}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 40 }}>
              {(post.tags || []).map((t) => (
                <span className="chip" key={t}><span className="dot" /> {t}</span>
              ))}
            </div>

            {post.coverImage && (
              <div style={{ borderRadius: "var(--radius-lg)", overflow: "hidden", marginBottom: 40, border: "1px solid var(--border)" }}>
                <img src={post.coverImage} alt={post.title} loading="eager" />
              </div>
            )}

            <div className="prose">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {post.markdown || ""}
              </ReactMarkdown>
            </div>

            <div className="cta reveal" style={{ marginTop: 80 }}>
              <span className="eyebrow">Liked it?</span>
              <h2 className="cta__title" style={{ fontSize: "clamp(28px, 4vw, 56px)" }}>
                Got a project?
                <br />
                <span className="it accent">Let's talk.</span>
              </h2>
              <Link className="btn btn--lg" to="/contact">
                Get in touch
                <span className="arrow"><HiArrowUpRight /></span>
              </Link>
            </div>
          </article>
        )}
      </div>
    </main>
  );
}
